import React, { useState } from 'react';
import { Users, Calendar, Clock, CheckCircle, Star, Send, Loader, AlertCircle, Phone, Mail } from 'lucide-react';

interface CateringFormData {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  eventDate: string;
  guestCount: string;
  budget: string;
  location: string;
  message: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

const Catering = () => {
  const [formData, setFormData] = useState<CateringFormData>({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    budget: '',
    location: '',
    message: ''
  });

  const [status, setStatus] = useState<FormStatus>({
    type: 'idle',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: 'Sending your catering inquiry...' });

    try {
      const response = await fetch('https://formspree.io/f/xblzozyr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          subject: `Catering Inquiry - ${formData.eventType}`,
          _subject: `Catering Inquiry from ${formData.name}`,
        }),
      });

      const data = await response.json();
      
      if (response.ok && !data.errors) {
        setStatus({
          type: 'success',
          message: 'Thank you! Your catering inquiry has been sent successfully. We\'ll contact you within 24 hours to discuss your event!'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          guestCount: '',
          budget: '',
          location: '',
          message: ''
        });
      } else {
        throw new Error(data.errors ? data.errors.map((e: any) => e.message).join(', ') : 'Failed to send inquiry');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your inquiry. Please try again or call us directly at (206) 492-7765.'
      });
    }
  };


  const eventTypes = [
    'Corporate Meeting',
    'Birthday Party',
    'Wedding Reception',
    'Graduation Party',
    'Office Lunch',
    'School Event',
    'Community Gathering',
    'Holiday Party',
    'Sports Event',
    'Other'
  ];

  return (
    <section id="catering" className="py-12 sm:py-16 bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-600 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-yellow-600 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-red-600">Catering</span> Services
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Make your next event unforgettable with authentic NY style flavors! From intimate gatherings 
            to large celebrations, we bring the taste of Italy directly to your venue.
          </p>
        </div>

        {/* Why Choose Us */}
        <div className="mb-8 sm:mb-12">
          <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-600/30 rounded-3xl p-8 sm:p-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8">
              Why Choose Hot Slice Pizza Catering?
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="bg-red-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Fresh & Hot</h4>
                <p className="text-gray-300 text-sm">All food prepared fresh and delivered hot to your event</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Professional Service</h4>
                <p className="text-gray-300 text-sm">Experienced team ensures seamless event execution</p>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Authentic NY Style</h4>
                <p className="text-gray-300 text-sm">Traditional recipes with premium halal ingredients</p>
              </div>
              
              <div className="text-center">
                <div className="bg-yellow-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Calendar className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Flexible Options</h4>
                <p className="text-gray-300 text-sm">Customizable menus to fit any event and budget</p>
              </div>
            </div>
          </div>
        </div>

        {/* Catering Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Form */}
          <div className="bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-6">Request a Catering Quote</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name and Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              {/* Phone and Event Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="(206) 123-4567"
                  />
                </div>
                
                <div>
                  <label htmlFor="eventType" className="block text-sm font-medium text-gray-300 mb-2">
                    Event Type *
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select event type</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Event Date and Guest Count */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-300 mb-2">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                
                <div>
                  <label htmlFor="guestCount" className="block text-sm font-medium text-gray-300 mb-2">
                    Number of Guests *
                  </label>
                  <select
                    id="guestCount"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select guest count</option>
                    <option value="10-25">10-25 guests</option>
                    <option value="25-50">25-50 guests</option>
                    <option value="50-100">50-100 guests</option>
                    <option value="100+">100+ guests</option>
                  </select>
                </div>
              </div>

              {/* Budget and Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select budget range</option>
                    <option value="$200-500">$200 - $500</option>
                    <option value="$500-1000">$500 - $1,000</option>
                    <option value="$1000-2000">$1,000 - $2,000</option>
                    <option value="$2000+">$2,000+</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-2">
                    Event Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="Event address or venue"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 resize-vertical"
                  placeholder="Tell us about your event, dietary restrictions, special requests, etc."
                />
              </div>

              {/* Status Message */}
              {status.type !== 'idle' && (
                <div className={`flex items-center space-x-2 p-4 rounded-xl ${
                  status.type === 'success' ? 'bg-green-600/20 border border-green-600 text-green-400' :
                  status.type === 'error' ? 'bg-red-600/20 border border-red-600 text-red-400' :
                  'bg-blue-600/20 border border-blue-600 text-blue-400'
                }`}>
                  {status.type === 'loading' && <Loader className="h-5 w-5 animate-spin" />}
                  {status.type === 'success' && <CheckCircle className="h-5 w-5" />}
                  {status.type === 'error' && <AlertCircle className="h-5 w-5" />}
                  <span className="text-sm font-medium">{status.message}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Request Quote</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-6">Get in Touch</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-red-600 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Call Us</h4>
                    <p className="text-gray-300">(206) 492-7765</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-600 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Email Us</h4>
                    <p className="text-gray-300 text-sm">hotslicepizzaseattle@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-600/30 rounded-2xl p-6 sm:p-8">
              <h4 className="text-lg font-bold text-white mb-4">Catering Guidelines</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Minimum order: 10 people</li>
                <li>• 48-hour advance notice preferred</li>
                <li>• Delivery available within 15 miles</li>
                <li>• Setup and cleanup services available</li>
                <li>• Custom menu options available</li>
                <li>• All food is prepared halal</li>
              </ul>
            </div>

            <div className="bg-yellow-600/20 border border-yellow-600/30 rounded-2xl p-6 sm:p-8">
              <h4 className="text-lg font-bold text-white mb-4">Popular Add-Ons</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Chicken wings (multiple flavors)</li>
                <li>• Fresh gelato dessert station</li>
                <li>• Vegetarian and gluten-free options</li>
                <li>• Disposable plates, napkins & utensils</li>
                <li>• Professional serving staff</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Catering;